import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Mail,
  MessageSquare,
  RefreshCw,
  Send,
  SkipForward,
  XCircle,
} from "lucide-react";
import {
  BuyEmailNotification,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import { formatOrderStatusInEmail } from "../../../utils/orderEmailStatusLabels";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./EmailNotifications.module.css";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const FILTER_DEBOUNCE_MS = 350;

const toTitleCase = (value: string | null | undefined) => {
  if (!value) return "-";
  return value
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getStatusClassName = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === "sent") return styles.statusSent;
  if (normalized === "failed") return styles.statusFailed;
  if (normalized === "skipped") return styles.statusSkipped;
  return styles.statusDefault;
};

const getRecipient = (item: BuyEmailNotification) =>
  item.channel === "sms" ? item.recipient_phone || "" : item.recipient_email || "";

const getMessagePreview = (item: BuyEmailNotification) => {
  const value = item.message_body || item.email_text || "";
  if (!value) return "-";
  return value.length > 140 ? `${value.slice(0, 137)}...` : value;
};

const getAttachmentLabel = (item: BuyEmailNotification) => {
  const parts: string[] = [];
  if (item.attachment_count > 0) {
    const types = item.attachment_types?.length ? item.attachment_types.map(toTitleCase).join(", ") : "Attachment";
    parts.push(`${types} (${item.attachment_count})`);
  }
  if (item.has_mobile_link) {
    parts.push(`Mobile link (${item.mobile_links?.length || 0})`);
  }
  return parts.length > 0 ? parts.join(" | ") : "-";
};

const BuyAdminEmailNotifications = () => {
  const [rows, setRows] = useState<BuyEmailNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ sent: 0, failed: 0, skipped: 0 });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  const [orderId, setOrderId] = useState("");
  const [recipient, setRecipient] = useState("");
  const [debouncedOrderId, setDebouncedOrderId] = useState("");
  const [debouncedRecipient, setDebouncedRecipient] = useState("");
  const [channel, setChannel] = useState("");
  const [orderStatusInEmail, setOrderStatusInEmail] = useState("");
  const [attachmentFilter, setAttachmentFilter] = useState("");
  const [orderStatusOptions, setOrderStatusOptions] = useState<string[]>([]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const offset = page * pageSize;

  const loadNotifications = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await buyAdminApi.getEmailNotifications({
        limit: pageSize,
        offset,
        orderId: debouncedOrderId || undefined,
        recipient: debouncedRecipient || undefined,
        channel,
        orderStatusInEmail,
        hasPdfAttachment: attachmentFilter === "pdf" ? "true" : undefined,
        hasMobileLink: attachmentFilter === "mobile_link" ? "true" : undefined,
        attachmentType: attachmentFilter === "pdf" ? "pdf" : undefined,
      });

      setRows(response.data);
      setTotal(response.pagination.total);
      setCounts(response.counts || { sent: 0, failed: 0, skipped: 0 });
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load email notifications.");
      setRows([]);
      setTotal(0);
      setCounts({ sent: 0, failed: 0, skipped: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextOrderId = orderId.trim();
      const nextRecipient = recipient.trim();
      if (nextOrderId === debouncedOrderId && nextRecipient === debouncedRecipient) {
        return;
      }
      setDebouncedOrderId(nextOrderId);
      setDebouncedRecipient(nextRecipient);
      setPage(0);
    }, FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [orderId, recipient, debouncedOrderId, debouncedRecipient]);

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, channel, orderStatusInEmail, attachmentFilter, debouncedOrderId, debouncedRecipient]);

  useEffect(() => {
    buyAdminApi.getEmailNotificationOrderStatuses()
      .then((response) => setOrderStatusOptions(response.data.filter(Boolean)))
      .catch(() => setOrderStatusOptions([]));
  }, []);

  const applyTextFilters = () => {
    setDebouncedOrderId(orderId.trim());
    setDebouncedRecipient(recipient.trim());
    setPage(0);
  };

  const handleReset = () => {
    setOrderId("");
    setRecipient("");
    setDebouncedOrderId("");
    setDebouncedRecipient("");
    setChannel("");
    setOrderStatusInEmail("");
    setAttachmentFilter("");
    setPage(0);
  };

  return (
    <div className={`${styles.page} buy-admin-theme-scope`}>
      <div className={styles.header}>
        <div>
          <div className={styles.titleRow}>
            <Mail size={22} />
            <h1>Buy Email Notifications</h1>
          </div>
          <p>Order email and SMS delivery records from the buy-side database.</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={loadNotifications}
            disabled={loading}
            aria-label="Refresh notifications"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : ""} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      <div className={styles.metrics}>
        <div className={`${styles.metric} ${styles.metricRevenue}`}>
          <div>
            <span>Total</span>
            <strong>{total}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconRevenue}`}>
            <MessageSquare size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricProfit}`}>
          <div>
            <span>Sent</span>
            <strong>{counts.sent}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconProfit}`}>
            <Send size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricPayout}`}>
          <div>
            <span>Failed</span>
            <strong>{counts.failed}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconPayout}`}>
            <XCircle size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricSkipped}`}>
          <div>
            <span>Skipped</span>
            <strong>{counts.skipped}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconSkipped}`}>
            <SkipForward size={18} />
          </div>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.inputGroup}>
          <label>Order</label>
          <input
            value={orderId}
            onChange={(event) => setOrderId(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") applyTextFilters(); }}
            placeholder="QA-MPW..."
          />
        </div>
        <div className={styles.inputGroup}>
          <label>Recipient</label>
          <input
            value={recipient}
            onChange={(event) => setRecipient(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") applyTextFilters(); }}
            placeholder="email or phone"
          />
        </div>
        <div className={styles.inputGroup}>
          <label>Channel</label>
          <select value={channel} onChange={(event) => { setChannel(event.target.value); setPage(0); }}>
            <option value="">All</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
        </div>
        <div className={styles.inputGroup}>
          <label>Order Status In Email</label>
          <select value={orderStatusInEmail} onChange={(event) => { setOrderStatusInEmail(event.target.value); setPage(0); }}>
            <option value="">All</option>
            {orderStatusOptions.map((status) => (
              <option key={status} value={status}>{formatOrderStatusInEmail(status)}</option>
            ))}
          </select>
        </div>
        <div className={styles.inputGroup}>
          <label>Content Type</label>
          <select value={attachmentFilter} onChange={(event) => { setAttachmentFilter(event.target.value); setPage(0); }}>
            <option value="">All</option>
            <option value="pdf">PDF attached</option>
            <option value="mobile_link">Mobile link</option>
          </select>
        </div>
        <button className={styles.resetButton} onClick={handleReset}>
          Reset
        </button>
      </div>

      {errorMessage && (
        <div className={styles.errorBanner}>
          <AlertCircle size={16} />
          {errorMessage}
        </div>
      )}

      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.emptyState}>Loading notifications...</div>
        ) : rows.length === 0 ? (
          <div className={styles.emptyState}>No notifications found.</div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Channel</th>
                  <th>Recipient</th>
                  <th>User Name</th>
                  <th>Order Status In Email</th>
                  <th>Message Text</th>
                  <th>Attachments / Links</th>
                  <th>Delivery Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.order_number || `#${row.order_id}`}</td>
                    <td>{toTitleCase(row.channel || "email")}</td>
                    <td className={styles.emailCell}>{getRecipient(row) || "-"}</td>
                    <td>{toTitleCase(row.user_name)}</td>
                    <td>{formatOrderStatusInEmail(row.order_status_in_email)}</td>
                    <td className={styles.messageCell} title={row.message_body || row.email_text || ""}>
                      {getMessagePreview(row)}
                    </td>
                    <td className={styles.attachmentCell}>{getAttachmentLabel(row)}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${getStatusClassName(row.delivery_status)}`}>
                        {row.delivery_status}
                      </span>
                      {row.error_message && <div className={styles.errorText}>{row.error_message}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.footer}>
          <span>Page {page + 1} of {totalPages}</span>
          <div className={styles.pagination}>
            <button disabled={page === 0 || loading} onClick={() => setPage((prev) => Math.max(0, prev - 1))}>
              <ChevronLeft size={16} />
            </button>
            <button disabled={page + 1 >= totalPages || loading} onClick={() => setPage((prev) => prev + 1)}>
              <ChevronRight size={16} />
            </button>
            <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size} / page</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyAdminEmailNotifications;
